import React from "react";
import { Box, Dropdown, Menu, MenuButton, Option, Tooltip } from "@mui/joy";
import { ColorLensIcon } from "../../styles/StyledMUI";
import DarkInput from "./DarkInput";
import DarkSelect from "./DarkSelect";
import IconButtonCTA from "./IconButtonCTA";
import { colorTokens, withAlpha } from "../../styles/globalStyles";
import type { NamedColor } from "../../utils/colorPalette";
import {
  COLOR_PALETTE_GROUP,
  COLOR_PALETTE_EXCURSION,
  GROUP_DEFAULT_COLOR,
  EXCURSION_DEFAULT_COLOR,
} from "../../utils/colorPalette";

const SWATCH_WIDTH = 12;
const GAP_PX = 8;
const CHAR_WIDTH_PX = 9;

function fixedWidth(palette: NamedColor[]) {
  return SWATCH_WIDTH + GAP_PX + Math.max(...palette.map((p) => p.name.length)) * CHAR_WIDTH_PX;
}

export function ColorSwatch({
  hex,
  size = 12,
  fallback,
}: {
  hex: string;
  size?: number;
  fallback: string;
}) {
  const safeHex = hex && String(hex).startsWith("#") ? hex : fallback;
  return (
    <span
      data-color-swatch
      role="presentation"
      style={{
        ["--swatch-color" as string]: safeHex,
        display: "inline-block",
        width: size,
        height: size,
        borderRadius: size <= 14 ? "50%" : 3,
        border: `1px solid ${withAlpha(colorTokens.text.primary, 0.6)}`,
        flexShrink: 0,
        backgroundColor: safeHex,
      }}
    />
  );
}

export type ColorPaletteProps = {
  value: string;
  onChange?: (hex: string) => void;
  onPick?: (hex: string) => void;
  /** "dropdown" = icon button + menu with grid; "select" = DarkSelect with swatch+name; "chip" = round swatch only, no text */
  mode?: "dropdown" | "select" | "chip";
  disabled?: boolean;
  palette?: NamedColor[];
  defaultColor?: string;
  onOpen?: () => void;
  onClose?: () => void;
  closeToken?: number;
  onBeforeOpen?: () => void;
  menuZIndex?: number;
  size?: "sm" | "md" | "lg";
  sx?: object;
  "aria-label"?: string;
};

export default function ColorPalette({
  value,
  onChange: onChangeProp,
  onPick,
  mode = "dropdown",
  disabled,
  palette = mode === "select" ? COLOR_PALETTE_EXCURSION : COLOR_PALETTE_GROUP,
  defaultColor,
  onOpen,
  onClose,
  closeToken,
  onBeforeOpen,
  menuZIndex = 1400,
  size = "sm",
  sx,
  "aria-label": ariaLabel,
}: ColorPaletteProps) {
  const onChange = onChangeProp ?? onPick;
  const defaultHex =
    defaultColor ??
    palette[0]?.hex ??
    (mode === "select" ? EXCURSION_DEFAULT_COLOR : GROUP_DEFAULT_COLOR);
  const fallback = defaultHex;
  const [hex, setHex] = React.useState<string>(value || fallback);

  React.useEffect(() => {
    setHex(value || fallback);
  }, [value, fallback]);

  const apply = React.useCallback(
    (c: string) => {
      setHex(c);
      onChange?.(c);
    },
    [onChange],
  );

  const [chipOpen, setChipOpen] = React.useState(false);
  React.useEffect(() => {
    if (closeToken != null) setChipOpen(false);
  }, [closeToken]);
  const handleChipOpenChange = React.useCallback(
    (_e: React.MouseEvent | React.KeyboardEvent | React.FocusEvent | null, isOpen?: boolean) => {
      setChipOpen(isOpen ?? false);
      if (isOpen) onOpen?.();
      else onClose?.();
    },
    [onOpen, onClose],
  );
  const handleChipApply = React.useCallback(
    (c: string) => {
      apply(c);
      setChipOpen(false);
      onClose?.();
    },
    [apply, onClose],
  );

  const colorGrid = (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 0.75 }}>
      {palette.map(({ hex: c, name }) => (
        <Tooltip key={c} title={name}>
          <Box
            onClick={() => apply(c)}
            sx={{
              width: 24,
              height: 24,
              borderRadius: 6,
              border: `1px solid ${colorTokens.border.default}`,
              cursor: "pointer",
            }}
            style={{ backgroundColor: c }}
          />
        </Tooltip>
      ))}
    </Box>
  );

  if (mode === "select") {
    const hexValue = value || fallback;
    return (
      <DarkSelect
        value={hexValue}
        onChange={(_, v) => v != null && onChange?.(String(v))}
        size={size}
        disabled={disabled}
        slotProps={{
          root: {
            sx: {
              backgroundColor: `${colorTokens.bg.surfaceMuted} !important`,
              color: `${colorTokens.text.primary} !important`,
              "& [data-color-swatch]": {
                backgroundColor: "var(--swatch-color) !important",
              },
            },
          },
        }}
        renderValue={(option) => {
          const v = option?.value ?? hexValue;
          const p = palette.find((x) => x.hex === v);
          if (!p) return <ColorSwatch hex={v} size={12} fallback={fallback} />;
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: colorTokens.text.primary,
              }}
            >
              <ColorSwatch hex={p.hex} size={12} fallback={fallback} />
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                {p.name}
              </Box>
            </Box>
          );
        }}
        sx={{
          width: { xs: "auto", sm: fixedWidth(palette) },
          minWidth: { xs: "auto", sm: fixedWidth(palette) },
          maxWidth: { xs: "auto", sm: fixedWidth(palette) },
          color: colorTokens.text.primary,
          ...sx,
        }}
      >
        {palette.map(({ hex: h, name }) => (
          <Option key={h} value={h}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: colorTokens.text.primary,
              }}
            >
              <ColorSwatch hex={h} size={14} fallback={fallback} />
              {name}
            </Box>
          </Option>
        ))}
      </DarkSelect>
    );
  }

  if (mode === "chip") {
    const chipSize = 20;
    const safeHex = (value && String(value).startsWith("#") ? value : fallback) || fallback;
    const chipColorGrid = (
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 0.75 }}>
        {palette.map(({ hex: c, name }) => (
          <Tooltip key={c} title={name}>
            <Box
              onClick={() => handleChipApply(c)}
              sx={{
                width: 24,
                height: 24,
                borderRadius: 6,
                border: `1px solid ${colorTokens.border.default}`,
                cursor: "pointer",
              }}
              style={{ backgroundColor: c }}
            />
          </Tooltip>
        ))}
      </Box>
    );
    return (
      <Dropdown key={closeToken ?? 0} open={chipOpen} onOpenChange={handleChipOpenChange}>
        <Tooltip title="Couleur">
          <MenuButton
            slots={{ root: "button" }}
            slotProps={{
              root: {
                "aria-label": ariaLabel ?? "Choisir une couleur",
                disabled,
                style: {
                  width: chipSize,
                  height: chipSize,
                  minWidth: chipSize,
                  padding: 0,
                  borderRadius: "50%",
                  border: `1px solid ${withAlpha(colorTokens.text.primary, 0.5)}`,
                  backgroundColor: safeHex,
                  cursor: disabled ? "default" : "pointer",
                },
              },
            }}
            onClick={() => onBeforeOpen?.()}
          />
        </Tooltip>
        <Menu
          slotProps={{
            root: { sx: { zIndex: menuZIndex } },
          }}
          sx={{
            p: 1,
            backgroundColor: colorTokens.bg.surface,
            border: `1px solid ${colorTokens.border.default}`,
            width: 220,
            zIndex: menuZIndex,
          }}
        >
          {chipColorGrid}
        </Menu>
      </Dropdown>
    );
  }

  return (
    <Dropdown
      key={closeToken ?? 0}
      onOpenChange={(_, isOpen?: boolean) => {
        isOpen ? onOpen?.() : onClose?.();
      }}
    >
      <Tooltip title="Couleur">
        <MenuButton
          slots={{ root: IconButtonCTA }}
          slotProps={{
            root: {
              "aria-label": ariaLabel ?? "Choisir une couleur",
              size: "sm",
              variant: "plain",
              disabled,
              onClick: () => onBeforeOpen?.(),
            },
          }}
        >
          <ColorLensIcon fontSize="small" />
        </MenuButton>
      </Tooltip>
      <Menu
        slotProps={{
          root: { sx: { zIndex: menuZIndex } },
        }}
        sx={{
          p: 1,
          backgroundColor: colorTokens.bg.surface,
          border: `1px solid ${colorTokens.border.default}`,
          color: colorTokens.text.secondary,
          width: 220,
          zIndex: menuZIndex,
        }}
      >
        {colorGrid}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}>
          <DarkInput
            size="sm"
            value={palette.find((p) => p.hex === hex)?.name ?? ""}
            readOnly
            sx={{ flex: 1 }}
          />
          <IconButtonCTA
            size="sm"
            variant="plain"
            aria-label="Appliquer la couleur"
            onClick={() => apply(hex)}
          >
            <ColorLensIcon fontSize="small" />
          </IconButtonCTA>
        </Box>
      </Menu>
    </Dropdown>
  );
}

export type ColorPaletteSelectProps = Omit<ColorPaletteProps, "mode"> & {
  palette?: NamedColor[];
  defaultColor?: string;
};

export function ColorPaletteSelect(props: ColorPaletteSelectProps) {
  return <ColorPalette {...props} mode="select" />;
}
